'use client';

import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';

import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';

import { useMutation } from '@tanstack/react-query';

import { useMemo, useState } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { ProfileContainer } from './profile-container';
import { ProfileHeading } from './profile-heading';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { CancelSubscriptionModal } from './cancel-subscription-modal';
import { FiCheck } from 'react-icons/fi';

type ProfileEditFormValues = z.infer<ReturnType<typeof profileEditSchema>>;
export function ProfileEditForm() {
  const customer = useCustomerStore(s => s.me!.customer);
  const authToken = useCustomerStore(s => s.me!.authToken);
  const [isOpenCancelSubscription, setIsOpenCancelSubscription] = useState(false);

  const [file, setFile] = useState<File | undefined>(undefined);

  const locale = useLocaleStore(s => s.currentLocale);

  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ProfileEditFormValues>({
    resolver: zodResolver(profileEditSchema(formValidationMessages)),
    defaultValues: {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      gender: customer?.gender ?? undefined,
      address: customer?.address ?? '',
      street_number: customer?.street_number ?? '',
      city: customer?.city ?? '',
      postcode: customer?.postcode ?? '',
      country: customer?.country ?? undefined,
      iban: customer?.iban ?? '',
      swift: customer?.swift ?? '',
      address_delivery: customer?.address_delivery ?? '',
      street_number_delivery: customer?.street_number_delivery ?? '',
      postcode_delivery: customer?.postcode_delivery ?? '',
      city_delivery: customer?.city_delivery ?? '',
      country_delivery: customer?.country_delivery ?? undefined,
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',
      organization_name: customer?.organization_name ?? '',
      role_in_organization: customer?.role_in_organization ?? undefined,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomer,
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await uploadFile({
          authToken,
          formData,
          locale,
        });
        data.image = res.data;
      } catch (e) {
        //TODO: handle error / create custom field for this error
        return;
      }
    }

    try {
      await mutateAsync({
        locale,
        data: data as any,
        authToken,
      }).then(res => {
        initCustomerStore({
          customer: res.data,
          authToken: authToken,
        });
      });
      toast({
        title: t('text3119'),
        variant: 'success',
      });
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
    }
  });

  return (
    <ProfileContainer>
      <ProfileHeading title={t('text1134')} />
      <Form {...form}>
        <form
          className={cn(
            'grid w-full max-w-[925px] grid-cols-12 gap-4 sm:gap-6 lg:gap-8',
            'ea-profile-edit-form',
          )}
          onSubmit={handleSubmit}
        >
          <div className="col-span-12 grid w-full grid-cols-12 gap-6 lg:gap-8">
            <div className="col-span-12 w-full sm:col-span-6">
              <Field
                required
                control={form.control}
                name="name"
                label={t('text33')}
                placeholder={t('text33')}
              />
            </div>
            <div className="col-span-12 w-full sm:col-span-6">
              <Field
                required
                control={form.control}
                name="surname"
                label={t('text34')}
                placeholder={t('text34')}
              />
            </div>
          </div>
          <div className="col-span-12 grid w-full grid-cols-12 gap-6 lg:gap-8">
            <div className="col-span-12 grid w-full grid-cols-12 gap-6 sm:col-span-6 lg:gap-8">
              <div className="col-span-12 w-full sm:col-span-7">
                <Field
                  control={form.control}
                  name="address"
                  label={t('text38')}
                  placeholder={t('text38')}
                />
              </div>
              <div className="col-span-12 w-full sm:col-span-5">
                <Field
                  control={form.control}
                  name="street_number"
                  label={t('text438')}
                  placeholder={t('text438')}
                />
              </div>
            </div>
            <div className="col-span-8 w-full sm:col-span-4">
              <Field
                control={form.control}
                name="city"
                label={t('text39')}
                placeholder={t('text39')}
              />
            </div>
            <div className="col-span-4 w-full sm:col-span-2">
              <Field
                control={form.control}
                name="postcode"
                label={t('text40')}
                placeholder={t('text40')}
              />
            </div>
          </div>
          <div className="col-span-12 grid w-full grid-cols-12 gap-6 lg:gap-8">
            <div className="col-span-12 w-full sm:col-span-6">
              <Field
                required
                control={form.control}
                name="email"
                type="email"
                label={t('text35')}
                placeholder={t('text35')}
                isDisabled={true}
              />
            </div>
            <div
              className={cn('col-span-12 w-full sm:col-span-6', 'ea-profile-edit__phone-container')}
            >
              <Field
                control={form.control}
                name="phone"
                type="tel"
                label={t('text36')}
                placeholder={t('text36')}
              />
            </div>
          </div>
          <div className="col-span-12 flex w-full flex-col items-start justify-start gap-4">
            <Field
              control={form.control}
              name="role_in_organization"
              label={t('text2213')}
              type="radio"
              options={[
                { value: t('text2211'), label: t('text2211') },
                { value: t('text2212'), label: t('text2212') },
              ]}
            />
          </div>
          <div className="col-span-12 w-full">
            <Field
              control={form.control}
              name="organization_name"
              type="text"
              label={t('text2214')}
              placeholder={t('ZUŠ Umelecká tanečná škola Bratislava')}
            />
          </div>
          {customer.newsletter_subscribed && (
            <div className="col-span-12 mt-4 flex w-full flex-col justify-between gap-4 md:flex-row md:items-center">
              <ResponsiveDialogProvider>
                <CancelSubscriptionModal
                  isOpen={isOpenCancelSubscription}
                  setIsOpen={setIsOpenCancelSubscription}
                />
              </ResponsiveDialogProvider>
              <h2 className="text-xl font-semibold text-foreground">{t('text3226')}</h2>
              <Button
                type="button"
                className="w-full md:w-fit md:px-8"
                onClick={() => setIsOpenCancelSubscription(true)}
              >
                {t('text3223')}
              </Button>
            </div>
          )}
          <div className="col-span-12 flex flex-row items-center justify-center">
            <Button
              type="submit"
              className="w-full md:w-fit"
              isLoading={form.formState.isSubmitting}
            >
              {t('text1952')}
            </Button>
          </div>
        </form>
      </Form>
    </ProfileContainer>
  );
}

type FieldProps = {
  control: Control<ProfileEditFormValues>;
  name: FieldPath<ProfileEditFormValues>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'] | 'tel' | 'radio';
  options?: { value: string | number; label: string }[];
  isFullWidth?: boolean;
  isDisabled?: boolean;
};

function Field(props: FieldProps) {
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="p-[1px]">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <Select
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={props.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {props.options?.map(o => (
                        <SelectItem key={o.value} value={String(o.value)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'radio') {
      if (!props.options) {
        throw new Error('Radio type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="flex flex-col gap-2 p-[1px]">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                    className="flex flex-row gap-4"
                  >
                    {props.options?.map(o => {
                      const isSelected = String(field.value) === String(o.value);
                      return (
                        <div key={o.value} className="relative">
                          <RadioGroupItem
                            id={`radiogroupitem-${o.value}`}
                            value={String(o.value)}
                            className="absolute h-0 w-0 opacity-0"
                          />
                          <Label htmlFor={`radiogroupitem-${o.value}`}>
                            <button
                              type="button"
                              onClick={() => field.onChange(o.value)}
                              disabled={props.isDisabled}
                              className={cn(
                                'rounded-none border px-6 py-3 text-base sm:text-lg lg:text-xl',
                                isSelected
                                  ? 'border-black bg-black text-white'
                                  : 'border-border bg-transparent text-black',
                                'transition-colors duration-200',
                              )}
                            >
                              {o.label}
                            </button>
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'tel') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required} className="!rounded-none">
              <FormLabel>{props.label}</FormLabel>
              <FormControl>
                <PhoneInputField
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  className="!rounded-none"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required} className="p-[1px]">
            <FormLabel>{props.label}</FormLabel>
            <FormControl>
              <Input
                placeholder={props.placeholder}
                type={props.type}
                {...(field as any)}
                disabled={props.isDisabled}
                aria-disabled={props.isDisabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.control,
    props.isDisabled,
    props.required,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.type,
  ]);

  return (
    <div
      className={cn('col-span-12 md:col-span-6', {
        ['md:col-span-12']: props.isFullWidth,
      })}
    >
      {content}
    </div>
  );
}

export const profileEditSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    username: z.string().optional(),
    address: z.string().optional(),
    street_number: z.string().optional(),
    organization_name: z.string().optional(),
    role_in_organization: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    company_buy: z.boolean().optional(),
    company: z.string().optional(),
    ico: z.string().optional(),
    dic: z.string().optional(),
    ic_dph: z.string().optional(),
    gender: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    image: z
      .object({
        id: z.number(),
      })
      .optional(),
    address_temporary: z.string().optional(),
    street_number_temporary: z.string().optional(),
    postcode_temporary: z.string().optional(),
    city_temporary: z.string().optional(),
    country_temporary: z
      .object({
        id: z.string(),
      })
      .optional(),
    email_company: z.string().email().optional(),
    address_company: z.string().optional(),
    street_number_company: z.string().optional(),
    postcode_company: z.string().optional(),
    city_company: z.string().optional(),
    country_company: z
      .object({
        id: z.string(),
      })
      .optional(),
    address_delivery: z.string().optional(),
    street_number_delivery: z.string().optional(),
    postcode_delivery: z.string().optional(),
    city_delivery: z.string().optional(),
    country_delivery: z
      .object({
        id: z.string(),
      })
      .optional(),
    name_delivery: z.string().optional(),
    surname_delivery: z.string().optional(),
    company_delivery: z.string().optional(),
    iban: z.string().optional(),
    swift: z.string().optional(),
    instagram: z.string().optional(),
    facebook: z.string().optional(),
    national_id_number: z.string().optional(),
  });
